/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

// Component imports
import { Button, Col, Input, Popconfirm, Row, Space, Table, Tooltip } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import TableCellTypography from '../UI/TableCellTypography';
import CreateIntegrationModal from '../Modals/CreateIntegrationModal';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import IntegrationForm from '../Forms/IntegrationForm';
import SideModal from '../Layout/SideModal';
import { EditIcon } from '../Icons/EditIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';
import { PlusIcon } from '../Icons/PlusIcon';

// Misc imports
import { useDeleteIntegrationMutation, useFetchIntegrationQuery, useFetchIntegrationsQuery } from '../../store/integrations/integrationsApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import { INTEGRATIONS, INTEGRATION_CATEGORY } from '../../constants/integrations';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import useDebouncedCallback from '../../utils/hooks/useDebounce';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';
import { DEFAULT_PAGINATION_SIZE } from '../../constants/main';

const OrganizationIntegrationsTable = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { handleMouseDown, isDragging } = useDragDetection();

  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateIntegrationModalOpen, setIsCreateIntegrationModalOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: integrations, isLoading: isLoadingIntegrations } = useFetchIntegrationsQuery({ orgId, urlParams:
    {
      page: searchParams.get('page') || currentPage - 1,
      pageSize: pageSize,
      ...(searchParams.get('searchTerm') && { searchTerm: searchParams.get('searchTerm') }),
      ...(searchParams.get('category') && { category: searchParams.get('category') }),
      ...(searchParams.get('type') && { type: searchParams.get('type') })
    } });
  const [deleteIntegration, { isLoading: isDeletingIntegration }] = useDeleteIntegrationMutation();
  const { data: integration } = useFetchIntegrationQuery({ orgId, id: searchParams.get('integrationId') }, { skip: !searchParams.get('integrationId') });
  const { organizationRoles, tenantRoles } = useSelector((state) => state.auth.currentUser);

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      ellipsis: true
    },
    {
      title: t('Integration'),
      dataIndex: 'type',
      key: 'type',
      responsive: ['sm'],
      sorter: (a, b) => a.type.localeCompare(b.type),
      filteredValue: searchParams.get('type') ? [searchParams.get('type')] : null,
      filters: Object.values(INTEGRATIONS)
        .filter((integration) => !integration.hidden)
        .map((integration) => {
          return {
            text: t(integration.type, { context: 'type' }),
            value: integration.type
          };
        }),
      filterMultiple: false,
      filterIcon: useTableFilterIcon,
      render: (text) => {
        const integration = Object.values(INTEGRATIONS).find((integration) => {
          return integration.type === text;
        });

        return <Space>
          {integration?.renderTypeIcon({ className: 'fs-xl' })} {t(integration?.type, { context: 'type' })}
        </Space>;
      }
    },
    {
      title: t('Scope'),
      dataIndex: 'category',
      key: 'category',
      responsive: ['sm'],
      filteredValue: searchParams.get('category') ? [searchParams.get('category')] : null,
      filters: Object.values(INTEGRATION_CATEGORY)
        .filter(category => INTEGRATIONS.some(integration => integration.category === category && !integration.hidden))
        .map(category => {
          return {
            text: t(category, { context: 'integration category' }),
            value: category
          };
        }),
      filterMultiple: false,
      filterIcon: useTableFilterIcon,
      render: text => (
        i18n.t(text, { context: 'integration category' })
      )
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      responsive: ['sm'],
      ellipsis: { showTitle: false },
      render: text => (
        <TableCellTypography>
          {text || '-'}
        </TableCellTypography>
      )
    },
    {
      title: t('Tags'),
      dataIndex: 'tags',
      key: 'tags',
      width: '15%',
      responsive: ['sm'],
      render: (_text, record) => {
        if (!record.tags || isEmptyArray(record.tags)) {
          return '-';
        }

        return (
          <ResponsiveTagsList
            values={record.tags.map((tag, index) => ({ label: tag, value: index }))}
            mode="tags"
            className="w-100"
            tagRender={(tag) => <span className="ant-select-selection-item p-0 px-1 mr-2">{tag.label}</span>}
          />
        );
      }
    },
    {
      title: t('Actions'),
      key: 'actions',
      align: 'center',
      width: 140,
      render: (_text, record) => (
        <Space onClick={(e) => e.stopPropagation()} size={4}>
          <Tooltip overlayClassName="pointer-events-none" title={t('Edit Integration')}>
            <Button
              disabled={!userHasEditAccess(orgId, organizationRoles, record.id, tenantRoles)}
              type="text"
              onClick={() => setSelectedIntegration(record)}
              icon={<EditIcon />}
              loading={isLoadingIntegrations || isDeletingIntegration}
              shape="circle"
            />
          </Tooltip>

          <Popconfirm
            disabled={!userHasEditAccess(orgId, organizationRoles, record.id, tenantRoles)}
            title={t('Delete Integration?')}
            okText={t('Delete')}
            cancelText={t('Cancel')}
            placement="topRight"
            onConfirm={() => {
              deleteIntegration({ orgId: orgId, id: record.id });
              updateUrlSearchParams();
              setCurrentPage(1);
            }}
          >
            <Tooltip overlayClassName="pointer-events-none" title={t('Delete Integration')}>
              <Button
                disabled={!userHasEditAccess(orgId, organizationRoles, record.id, tenantRoles)}
                shape="circle"
                type="text"
                icon={<DeleteIcon />}
                loading={isLoadingIntegrations || isDeletingIntegration}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  useEffect(() => {
    if (orgId) {
      updateUrlSearchParams({ current: Number(searchParams.get('page')) + 1 });
    }
  }, [orgId]);

  useEffect(() => {
    if (integration) {
      setSelectedIntegration(integration);
    }
  }, [integration]);

  useEffect(() => {
    updateUrlSearchParams({ current: Number(searchParams.get('page')) + 1, integrationId: selectedIntegration?.id });
  }, [selectedIntegration]);

  const handleTableChange = async (pagination, filters, sorter) => {
    let sorting;

    if (sorter.column) {
      sorting = { [sorter.columnKey]: sorter.order === 'ascend' ? 'asc' : 'desc' };
    } else {
      if (sorter.field === 'createdAt' && searchParams.get('order') === 'desc') {
        sorting = { [sorter.columnKey]: 'asc' };
      }
    }

    updateUrlSearchParams({ current: pagination.current, pageSize: pagination.pageSize, filters, sorting });
  };

  const openCreateIntegrationModal = () => {
    setIsCreateIntegrationModalOpen(true);
  };

  const closeCreateIntegrationModal = () => {
    setIsCreateIntegrationModalOpen(false);
  };

  const closeSideModal = () => {
    setSelectedIntegration(null);
    updateUrlSearchParams({ current: Number(searchParams.get('page')) + 1, pageSize, integrationId: null });
  };

  const updateUrlSearchParams = async ({ searchTerm = null, current = 1, pageSize = DEFAULT_PAGINATION_SIZE, filters = { type: [searchParams.get('type')], category: searchParams.get('category') }, sorting = { createdAt: 'desc' }, integrationId }) => {
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);
    urlSearchParams.set('page', current - 1);
    const integId = integrationId !== undefined ? integrationId : searchParams.get('integrationId');

    if (searchTerm) {
      urlSearchParams.set('searchTerm', encodeURIComponent(searchTerm));
    } else {
      urlSearchParams.set('searchTerm', encodeURIComponent(''));
    }

    if (filters?.type) {
      urlSearchParams.set('type', filters?.type);
    } else {
      urlSearchParams.delete('type');
    }

    if (filters?.category) {
      urlSearchParams.set('category', filters?.category);
    } else {
      urlSearchParams.delete('category');
    }

    if (sorting) {
      urlSearchParams.set('sortBy', Object.entries(sorting).flat()[0]);
      urlSearchParams.set('order', Object.entries(sorting).flat()[1]);
    } else {
      urlSearchParams.delete('sortBy');
      urlSearchParams.delete('order');
    }

    if (integId) {
      urlSearchParams.set('integrationId', integId);
    } else {
      urlSearchParams.delete('integrationId');
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const debouncedOnChange = useDebouncedCallback(updateUrlSearchParams);

  return (
    <Fragment>
      <Row className="mb-4">
        <Col span={4}>
          <Input
            prefix={<SearchOutlined className="text-secondary" />}
            size="large"
            hidden // Remove the hidden property when backend supports search
            placeholder={t('Search')}
            allowClear
            onChange={(e) => debouncedOnChange(e.target.value)}
          />
        </Col>

        {userHasEditAccess(orgId, organizationRoles, null, tenantRoles)
          ? <Col span={12} offset={8}>
            <Button icon={<PlusIcon />} onClick={() => openCreateIntegrationModal()} className="float-right" type="primary" size="large">
              {t('Add Integration')}
            </Button>
          </Col>
          : null}
      </Row>

      <Table
        rowKey="id"
        columns={columns}
        onRow={(record) => ({
          onMouseDown: handleMouseDown,
          onClick: () => {
            if(!isDragging() && userHasEditAccess(orgId, organizationRoles, record.id, tenantRoles)) {
              setSelectedIntegration(record);
            }
          }
        })}
        rowClassName="cursor-pointer"
        pagination={{
          total: integrations?.total || 0,
          defaultCurrent: 1,
          current: currentPage,
          pageSize: integrations?.pageSize || pageSize,
          showSizeChanger: true,
          size: 'default',
          showTotal: (total) => `${t('Total Integrations')}: ${total}`
        }}
        onChange={handleTableChange}
        dataSource={integrations?.items}
        loading={isLoadingIntegrations}
      />

      <CreateIntegrationModal
        isModalOpen={isCreateIntegrationModalOpen}
        setIntegration={setSelectedIntegration}
        closeModal={closeCreateIntegrationModal}
      />

      <SideModal
        open={!!selectedIntegration}
        onClose={closeSideModal}
        title={t('Edit Integration')}
        bodyClassName="p-0"
        destroyOnClose
      >
        <IntegrationForm
          integration={selectedIntegration}
          bodyClassName="px-6 pt-6 flex-grow-1"
          footerClassName="ant-drawer-footer"
          onCancel={closeSideModal}
          onSuccess={(response) => {
            if (!selectedIntegration?.id && response?.id) {
              setSelectedIntegration(response);
              closeCreateIntegrationModal();
            }
          }}
        />
      </SideModal>
    </Fragment>
  );
};

export default OrganizationIntegrationsTable;
